import { useEffect, useMemo, useState } from 'react'
import { Search, MessageSquare, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'

interface Conversation {
  id: string
  channelKind: string
  externalThreadId: string
  status: string
  lastMessageAt: string
  lastMessagePreview: string | null
  unreadCount: number
  clientStub?: {
    id: string
    displayName: string
    email: string | null
    phoneE164: string | null
  } | null
}

type Status = 'ALL' | 'OPEN' | 'SNOOZED' | 'CLOSED'

interface ConversationListProps {
  selectedId?: string
  onSelect: (id: string) => void
  pollMs?: number
}

const POLL_MS_DEFAULT = 6000

const CHANNELS = ['ALL', 'EMAIL', 'SMS', 'WHATSAPP', 'VOICE', 'INSTAGRAM', 'FACEBOOK', 'WEBCHAT']

export function ConversationList({
  selectedId,
  onSelect,
  pollMs = POLL_MS_DEFAULT,
}: ConversationListProps) {
  const [items, setItems] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<Status>('OPEN')
  const [channel, setChannel] = useState<string>('ALL')

  async function load() {
    setLoading(true)
    setErr(null)
    try {
      const params = new URLSearchParams()
      if (status !== 'ALL') params.set('status', status)
      if (channel !== 'ALL') params.set('channelKind', channel)
      const qs = params.toString() ? `?${params.toString()}` : ''
      const data = await api<{ items: Conversation[] }>(
        `/v1/marketing/inbox/conversations${qs}`,
      )
      setItems(data.items)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), pollMs)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, channel, pollMs])

  const filtered = useMemo(() => {
    if (!search.trim()) return items
    const q = search.trim().toLowerCase()
    return items.filter((c) => {
      const name = c.clientStub?.displayName ?? ''
      const email = c.clientStub?.email ?? ''
      const phone = c.clientStub?.phoneE164 ?? ''
      return (
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        phone.toLowerCase().includes(q) ||
        c.externalThreadId.toLowerCase().includes(q) ||
        (c.lastMessagePreview ?? '').toLowerCase().includes(q)
      )
    })
  }, [items, search])

  return (
    <div className="flex flex-col h-full bg-white border-r border-ink-900/10">
      <div className="sticky top-0 bg-white border-b border-ink-900/10 p-3 space-y-2 z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium tracking-tight">Inbox</h2>
          <button
            className="text-ink-700/60 hover:text-blueprint-700"
            onClick={() => void load()}
            disabled={loading}
            title="Refrescar"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="relative">
          <Search className="size-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-ink-700/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar"
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-ink-900/10 rounded bg-surface focus:outline-none focus:border-blueprint-600"
          />
        </div>
        <div className="flex gap-1">
          {(['OPEN', 'SNOOZED', 'CLOSED', 'ALL'] as Status[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={[
                'px-2 py-1 text-[10px] font-mono rounded',
                status === s
                  ? 'bg-blueprint-600 text-white'
                  : 'bg-surface text-ink-700/60 hover:text-ink-900',
              ].join(' ')}
            >
              {s}
            </button>
          ))}
        </div>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="w-full px-2 py-1 text-xs font-mono border border-ink-900/10 rounded bg-surface focus:outline-none focus:border-blueprint-600"
        >
          {CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c === 'ALL' ? 'Todos los canales' : c}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-auto">
        {err && (
          <div className="p-3 text-xs font-mono text-red-700 border-b border-red-200 bg-red-50">
            {err}
          </div>
        )}
        {!err && filtered.length === 0 && !loading && (
          <div className="p-6 text-center text-xs text-ink-700/60">
            <MessageSquare className="size-5 mx-auto mb-2 opacity-50" />
            Sin conversaciones.
          </div>
        )}
        <ul>
          {filtered.map((c) => {
            const active = selectedId === c.id
            return (
              <li key={c.id}>
                <button
                  data-testid="conversation-item"
                  data-conversation-id={c.id}
                  onClick={() => onSelect(c.id)}
                  className={[
                    'w-full text-left px-3 py-2 border-b border-ink-900/5 hover:bg-surface-alt',
                    active ? 'bg-blueprint-50 border-l-2 border-l-blueprint-600' : '',
                  ].join(' ')}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="bp-pill text-[10px]">{c.channelKind}</span>
                    <span className="text-xs font-medium truncate flex-1">
                      {c.clientStub?.displayName ?? c.externalThreadId}
                    </span>
                    {c.unreadCount > 0 && (
                      <span className="bp-pill bg-blueprint-600 text-white text-[10px]">
                        {c.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-700/60 truncate">
                    {c.lastMessagePreview ?? '(sin mensajes)'}
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
